import { Avatar } from '@amore/ui/components/avatar'
import { Badge } from '@amore/ui/components/badge'
import { buttonStyles } from '@amore/ui/components/button'
import { Card, CardHeader, CardTitle } from '@amore/ui/components/card'
import { Heading, Text } from '@amore/ui/components/typography'
import { Link } from '@tanstack/react-router'
import { ArrowRight, CalendarDays, Check, CloudSun, Heart, Route, Users } from 'lucide-react'
import type { Ref } from 'react'
import { formatDate, initials } from '@/lib/format'
import { supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import { useWedding } from '@/mock/store'
import { EmptyState } from '@/patterns/empty-state'
import { CeremonyEditAction } from './edit-action'
import { ceremonyKindLabels, ceremonySummary } from './model'

export function CeremonySummary({
  onEdit,
  editButton,
}: {
  onEdit: () => void
  editButton: Ref<HTMLButtonElement>
}) {
  const data = useWedding()
  const { event, venue } = ceremonySummary(data)
  return (
    <Card padding="none" className="overflow-hidden">
      <div className="grid md:grid-cols-[minmax(0,1fr)_13rem]">
        <div className="flex flex-col justify-center gap-3 bg-panel-accent p-4 sm:px-5">
          <div className="flex items-start justify-between gap-3">
            <Heading as="h2" size="md" className="min-w-0 break-words sm:text-2xl">
              {event?.location || venue?.name || 'Kde si řeknete ano?'}
            </Heading>
            <CeremonyEditAction
              ref={editButton}
              label="Upravit základní údaje obřadu"
              onClick={onEdit}
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
            {venue ? (
              <Link
                to={`/suppliers/${venue.id}`}
                className="inline-flex min-w-0 items-center gap-2 text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
              >
                <span className="break-words">{event?.location ? venue.name : 'Detail místa'}</span>
                <ArrowRight className="size-3.5 shrink-0" aria-hidden="true" />
              </Link>
            ) : null}
            <p className="flex items-center gap-2 text-muted-foreground">
              <CalendarDays className="size-3.5 shrink-0" aria-hidden="true" />
              {data.identity.date
                ? formatDate(data.identity.date, 'long')
                : 'Datum zatím nevybrané'}
            </p>
            <p className="flex items-center gap-2 text-muted-foreground">
              <Heart className="size-3.5 shrink-0" aria-hidden="true" />
              {data.ceremony.kind ? (
                <>
                  <span className="sr-only">Typ obřadu: </span>
                  {ceremonyKindLabels[data.ceremony.kind]}
                </>
              ) : (
                'Typ zatím nevybraný'
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3 md:flex-col md:justify-center md:gap-2 md:border-t-0 md:border-l md:px-5 md:py-4 md:text-center">
          <div>
            <Text variant="label" tone="muted">
              Začátek obřadu
            </Text>
            <p className="mt-1 font-heading text-3xl leading-none tracking-tight text-action md:text-4xl">
              {event?.time || '—:—'}
            </p>
          </div>
          <div className="flex min-w-0 flex-col items-end gap-1 md:items-center">
            {event?.endTime ? (
              <Text variant="small" tone="muted" className="whitespace-nowrap">
                Přibližně do {event.endTime}
              </Text>
            ) : (
              <Text variant="small" tone="muted">
                Čas zatím otevřený
              </Text>
            )}
            {event ? (
              <Link
                to={`/schedule/${event.id}`}
                className="inline-flex min-w-0 items-center gap-1.5 text-xs font-semibold text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
              >
                <span>{event.removed ? 'Odebráno z harmonogramu' : 'V harmonogramu'}</span>
                <ArrowRight className="size-3 shrink-0" aria-hidden="true" />
              </Link>
            ) : null}
          </div>
        </div>
      </div>
    </Card>
  )
}

export function CeremonyScenario() {
  const { ceremony } = useWedding()
  return (
    <Card padding="none" className="overflow-hidden">
      <CardHeader appearance="strip">
        <div className="flex items-center gap-2.5">
          <Route className="size-4 text-action" aria-hidden="true" />
          <CardTitle as="h2">Jak půjde obřad za sebou</CardTitle>
        </div>
        {ceremony.steps.length > 0 ? (
          <Badge tone="neutral">{ceremony.steps.length} bodů</Badge>
        ) : null}
      </CardHeader>
      {ceremony.steps.length ? (
        <ol className="px-5 py-2 sm:px-6">
          {ceremony.steps.map((step, index) => (
            <li
              key={step.id}
              className="relative flex gap-4 py-4 not-last:after:absolute not-last:after:top-12 not-last:after:bottom-[-1rem] not-last:after:left-[0.9375rem] not-last:after:w-px not-last:after:bg-border"
            >
              <span className="z-1 grid size-8 shrink-0 place-items-center rounded-full border border-border bg-background text-xs font-semibold text-action tabular-nums">
                {index + 1}
              </span>
              <div className="min-w-0 pt-1">
                <h3 className="text-sm font-semibold">{step.title}</h3>
                {step.description ? (
                  <Text tone="muted" className="mt-1">
                    {step.description}
                  </Text>
                ) : null}
              </div>
            </li>
          ))}
        </ol>
      ) : (
        <div className="p-5">
          <EmptyState
            placement="inline"
            icon={<Route />}
            title="Obřad podle vás"
            description="Přidejte jen to, co k vašemu obřadu patří. Pořadí i názvy bodů si určíte sami."
          />
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-5 py-3">
        <Text variant="small" tone="muted">
          {ceremony.steps.length
            ? 'Stručný náhled vašeho scénáře.'
            : 'Žádný scénář není předvybraný.'}
        </Text>
        <Link
          to="/ceremony/scenario"
          className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
        >
          {ceremony.steps.length ? 'Upravit scénář' : 'Sestavit scénář'}
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>
    </Card>
  )
}

export function CeremonyPeople() {
  const data = useWedding()
  const { officiant, brideWitness, groomWitness } = ceremonySummary(data)
  const witnesses = [
    {
      role: data.identity.brideName
        ? `Svědek · ${data.identity.brideName}`
        : 'Svědek prvního z páru',
      guest: brideWitness,
    },
    {
      role: data.identity.groomName
        ? `Svědek · ${data.identity.groomName}`
        : 'Svědek druhého z páru',
      guest: groomWitness,
    },
  ]
  return (
    <Card padding="none" className="overflow-hidden">
      <CardHeader appearance="strip">
        <div className="flex items-center gap-2.5">
          <Users className="size-4 text-action" aria-hidden="true" />
          <CardTitle as="h2">Kdo bude při vás</CardTitle>
        </div>
      </CardHeader>
      <div className="divide-y divide-border px-5">
        <div className="flex items-center gap-3 py-4">
          <Avatar
            alt=""
            fallback={officiant ? initials(officiant.name.replace('Mgr. ', '')) : '?'}
          />
          <div className="min-w-0 flex-1 space-y-1">
            <Text variant="small" tone="muted">
              Oddávající
            </Text>
            {officiant ? (
              <Link
                to={`/suppliers/${officiant.id}`}
                className="block text-sm font-semibold text-action underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring"
              >
                {officiant.name}
              </Link>
            ) : (
              <Text>Zatím nevybraný</Text>
            )}
            {officiant ? (
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={supplierStatusTone(officiant.status)}>
                  {supplierStatusLabels[officiant.status]}
                </Badge>
                {data.ceremony.officiantArrival ? (
                  <Text variant="small" tone="muted">
                    Příjezd {data.ceremony.officiantArrival}
                  </Text>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        {witnesses.map(({ role, guest }) => (
          <div key={role} className="flex items-center gap-3 py-4">
            <Avatar alt="" fallback={guest ? initials(guest.name) : '?'} />
            <div className="min-w-0 flex-1 space-y-1">
              <Text variant="small" tone="muted">
                {role}
              </Text>
              {guest ? (
                <Link
                  to={`/guests/${guest.partyId}`}
                  className="block text-sm font-semibold underline-offset-4 hover:text-action hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                >
                  {guest.name}
                </Link>
              ) : (
                <Text>Zatím nevybraný</Text>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-end border-t border-border px-5 py-2">
        <Link
          to="/ceremony/people"
          className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
        >
          Lidé a další role
          <ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>
    </Card>
  )
}

export function CeremonyPlanB() {
  const data = useWedding()
  const { planB } = data.ceremony
  const { planBDecider } = ceremonySummary(data)
  const complete = Boolean(planB?.location && planB.decisionTime && planBDecider)
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <CloudSun className="size-4 text-action" aria-hidden="true" />
          <CardTitle as="h2">Kdyby pršelo</CardTitle>
        </div>
        <Badge tone={complete ? 'success' : 'warning'}>
          {complete ? (
            <>
              <Check className="size-3" aria-hidden="true" />
              Domluveno
            </>
          ) : planB ? (
            'Ještě doladit'
          ) : (
            'Zatím otevřené'
          )}
        </Badge>
      </div>
      <p className="mt-4 text-sm font-semibold">
        {planB?.location || 'Záložní varianta zatím není vybraná'}
      </p>
      <Text tone="muted" className="mt-1">
        {complete
          ? `${planBDecider?.name} rozhodne o přesunu v ${planB?.decisionTime}.`
          : planB
            ? 'Místo máme. Ještě zbývá určit, kdo a kdy rozhodne o přesunu.'
            : 'Pokud se vás počasí týká, můžete si připravit náhradní místo a domluvit přesun.'}
      </Text>
      <div className="mt-3 flex justify-end">
        <Link
          to="/ceremony/place/plan-b"
          className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
        >
          Plán B<ArrowRight className="size-3.5" aria-hidden="true" />
        </Link>
      </div>
    </Card>
  )
}
